import { useState, useLayoutEffect } from 'react';
import ReviewImage from './ReviewImage';
import CommentRatingStar from '../CommentRatingStar';
import useStyles from './useStyles';
import getPriceRange from '../getPriceRange';

import { CustomizedTypography } from '@components/Customized';
import { clsx, colorSet } from '@components/Provider';

import { useMediaQuery, useTheme } from '@material-ui/core';

import { ChevronDown, UserAvatarFilled } from '@carbon/icons-react';
import { CapaRfqReview } from '@generated/graphql';

import {
  getFileUrl,
  formatDate,
  maskingString,
  requestEnumToString
} from '@utils';

interface Props {
  review?: CapaRfqReview;
  index: number;
}

const WrittenReviewCommentItem = ({ index, review }: Props) => {
  const classes = useStyles();
  const [elipsis, setElipsis] = useState(false);
  const [more, setMore] = useState(false);

  const matches = useMediaQuery(useTheme().breakpoints.down('xs'));

  const reviewFiles = review?.reviewFiles ?? [];

  const images = reviewFiles.map((file) => getFileUrl(file?.fileId ?? ''));

  const purchaseOrderValue = review?.purchaseOrderValue ?? 0;

  const noImage = images.length === 0;

  const multipleImages = images.length > 1;

  const handleImageExpand = (value: boolean) => {
    setMore(value);
  };

  useLayoutEffect(() => {
    const textContents = document.getElementById(`text__contents-${index}`);

    const scrollHeight = textContents?.scrollHeight ?? 0;
    const clientHeight = textContents?.clientHeight ?? 0;

    if (scrollHeight > clientHeight) {
      setElipsis(true);
    }
  }, [index]);

  const isRenderMoreButton = () => {
    if (noImage) {
      // 이미지가 없으면
      return elipsis; //3줄 이상일때는 렌더
    } else {
      // 이미지가 있을때

      if (matches) {
        // 모바일 O
        if (images.length === 1 && elipsis) return true;
        if (images.length === 1 && !elipsis) return false;

        if (images.length > 1) return true;

        return elipsis;
      } else {
        // 모바일 X
        if (images.length >= 1) return true;

        return elipsis;
      }
    }
  };

  return (
    <div className={classes.comment}>
      <div className={classes.avatar}>
        {review?.profileId ? (
          <img src={getFileUrl(review.profileId)} alt="" />
        ) : (
          <UserAvatarFilled size={32} color={colorSet.gray500} />
        )}
      </div>

      <div className={classes.item}>
        <div className={classes.company}>
          <div className={classes.rating}>
            <CommentRatingStar rating={review?.reviewPoint ?? 0} />
            <CustomizedTypography
              className="createdAt"
              color="textSecondary"
              variant="body2"
            >
              {review?.reviewedAt
                ? formatDate(review?.reviewedAt, 'YY.MM.DD')
                : ''}
            </CustomizedTypography>
          </div>

          <CustomizedTypography
            variant="body2"
            fontWeight="500"
            gutterBottoms={2}
          >
            {maskingString(
              review?.organizationName,
              review?.organizationName?.length === 1
                ? 4
                : review?.organizationName?.length
            )}
          </CustomizedTypography>
          <CustomizedTypography
            variant="subtitle2"
            color="textSecondary"
            gutterBottoms={2}
          >
            발주액 {getPriceRange(purchaseOrderValue)}&nbsp;
            {review?.indueDate ? `· 납기일 준수` : `· 납기일 미준수`}
          </CustomizedTypography>
          <CustomizedTypography variant="subtitle2" color="textSecondary">
            {requestEnumToString(review?.serviceType)} ·{' '}
            {maskingString(
              review?.projectName,
              review?.projectName?.length === 1
                ? 4
                : review?.projectName?.length
            )}
          </CustomizedTypography>
        </div>

        <div className={classes.contents}>
          <div
            className={clsx('inner__contents', {
              'inner__contents-more': more,
              'inner__contents-no_img': noImage
            })}
          >
            <CustomizedTypography
              id={`text__contents-${index}`}
              className={clsx('breakAll', {
                more: more,
                elipsis: elipsis && !more
              })}
              variant="body1"
              color="textPrimary"
              fontWeight="500"
            >
              {review?.reviewComment}
            </CustomizedTypography>

            {review?.reviewComment && isRenderMoreButton() && (
              <div
                className={classes.more}
                onClick={() => handleImageExpand(!more)}
              >
                <CustomizedTypography
                  variant="body2"
                  color="textPrimary"
                  fontWeight="500"
                >
                  {more ? '접기' : '더보기'}
                </CustomizedTypography>
                <ChevronDown
                  size={20}
                  className={more ? 'animation' : undefined}
                />
              </div>
            )}
          </div>

          {!matches && !noImage && (
            <>
              {!more && (
                <ReviewImage
                  matches={matches}
                  mode="preview"
                  src={images[0]}
                  max={images.length}
                  onClick={() => handleImageExpand(!more)}
                />
              )}
            </>
          )}
        </div>

        {!matches && !noImage && (
          <>
            {more &&
              images.map((img, index) => (
                <ReviewImage
                  key={img}
                  index={index}
                  matches={matches}
                  max={images.length}
                  mode="more"
                  gutterBottom={index !== images.length - 1}
                  src={img}
                />
              ))}
          </>
        )}

        {matches && !noImage && (
          <>
            {multipleImages ? (
              <>
                {!more && (
                  <div className={classes.images}>
                    <ReviewImage
                      className="left__radius"
                      matches={matches}
                      mode="preview"
                      max={images.length}
                      src={images[0]}
                    />

                    <ReviewImage
                      className="right__radius"
                      matches={matches}
                      mode="preview"
                      src={images[1]}
                      multipleImages
                      max={images.length}
                      onClick={() => handleImageExpand(!more)}
                    />
                  </div>
                )}

                {more &&
                  images.map((img, index) => (
                    <ReviewImage
                      key={img}
                      index={index}
                      max={images.length}
                      matches={matches}
                      mode="more"
                      gutterBottom={index !== images.length - 1}
                      src={img}
                    />
                  ))}
              </>
            ) : (
              <ReviewImage
                matches={matches}
                mode="more"
                src={images[0]}
                max={images.length}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default WrittenReviewCommentItem;
